import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  FileText,
  LoaderCircle,
  Plus,
  Sparkles,
} from "lucide-react";
import { useEffect, useState } from "react";
import { PageHeader, useRouter } from "@tribunalea/ui";
import { useApi } from "../App.jsx";

export function LibraryPage({ management = false }) {
  const api = useApi();
  const { navigate } = useRouter();
  const [notebooks, setNotebooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    api.listNotebooks()
      .then((items) => active && setNotebooks(items))
      .catch((reason) => active && setError(reason.message))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [api]);

  async function createNotebook(event) {
    event.preventDefault();
    if (!name.trim() || creating) return;
    setCreating(true);
    setError("");
    try {
      const notebook = await api.createNotebook({
        name: name.trim(),
        description: description.trim(),
      });
      navigate(
        management
          ? `/biblioteca/administrar/${notebook.id}`
          : `/biblioteca/${notebook.id}`,
      );
    } catch (reason) {
      setError(reason.message);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="page library-page">
      {management && (
        <button
          className="back-link"
          type="button"
          onClick={() => navigate("/biblioteca")}
        >
          <ArrowLeft size={16} />
          Biblioteca IA
        </button>
      )}
      <PageHeader
        hero
        eyebrow="Biblioteca IA · Administración"
        title="Administrá tus cuadernos."
        description="Creá cuadernos y gestioná los PDFs que Justiniano utiliza como fuentes de consulta."
        actions={(
          <button
            data-justiniano-target="library-create-notebook"
            className="primary-button"
            type="button"
            onClick={() => setShowForm((value) => !value)}
          >
            <Plus size={17} />
            Nuevo cuaderno
          </button>
        )}
      />

      {showForm && (
        <form className="notebook-create-card" onSubmit={createNotebook}>
          <div>
            <span className="card-icon"><BookOpen size={20} /></span>
            <div>
              <strong>Crear un cuaderno</strong>
              <p>Después vas a poder agregar y procesar sus fuentes.</p>
            </div>
          </div>
          <label>
            Nombre
            <input
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={120}
              placeholder="Ej. Ejecuciones fiscales"
            />
          </label>
          <label>
            Descripción <span>Opcional</span>
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={500}
              placeholder="Qué documentos reúne y para qué lo vas a usar."
            />
          </label>
          <div className="notebook-form-actions">
            <button
              className="secondary-button"
              type="button"
              onClick={() => setShowForm(false)}
            >
              Cancelar
            </button>
            <button
              className="primary-button"
              type="submit"
              disabled={!name.trim() || creating}
            >
              {creating
                ? <LoaderCircle className="is-spinning" size={16} />
                : <Plus size={16} />}
              Crear cuaderno
            </button>
          </div>
        </form>
      )}

      {error && <p className="form-error">{error}</p>}

      {loading ? (
        <div className="loading-card">
          <LoaderCircle className="is-spinning" size={18} />
          Cargando biblioteca…
        </div>
      ) : notebooks.length ? (
        <section className="notebook-grid" aria-label="Cuadernos">
          {notebooks.map((notebook) => (
            <button
              className="notebook-card"
              key={notebook.id}
              type="button"
              onClick={() => navigate(
                management
                  ? `/biblioteca/administrar/${notebook.id}`
                  : `/biblioteca/${notebook.id}`,
              )}
            >
              <span className="notebook-card-icon">
                <BookOpen size={23} />
              </span>
              <span className="notebook-card-copy">
                <strong>{notebook.name}</strong>
                <span>{notebook.description || "Sin descripción"}</span>
                <small>
                  <FileText size={13} />
                  {notebook.readySourceCount} de {notebook.sourceCount} fuentes listas
                  <i>·</i>
                  {notebook.pageCount} páginas
                </small>
              </span>
              <ArrowRight size={18} />
            </button>
          ))}
        </section>
      ) : (
        <div className="empty-panel library-empty">
          <Sparkles size={26} />
          <h3>Todavía no creaste cuadernos</h3>
          <p>Creá el primero para convertir tus documentos en fuentes consultables.</p>
          <button
            className="primary-button"
            type="button"
            onClick={() => setShowForm(true)}
          >
            <Plus size={16} />
            Crear cuaderno
          </button>
        </div>
      )}
    </div>
  );
}
